import {useContext, useMemo} from 'react';

import {useResilientQuery} from '@famly/stat_apollo-query';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {type MediaTaggedQuery, type MediaTaggedQueryVariables} from '../__generated__/queries.api-types';
import {FileTaggingContext} from '../file-tagging-context';
import {useSubscribeFileTagging} from '../use-subscribe-file-tagging';
import {getMediaTaggedVariables, hasTaggedChildren} from '../helpers';
import {MediaTagged} from '../queries';
import {useSelectedFile} from './use-selected-file';

export const useMediaTagged = () => {
    const {taggedFiles, selectedTaggedFileIndex, didInitTaggedMedia} = useSubscribeFileTagging(state => state);

    const {initTaggedChildren} = useContext(FileTaggingContext);

    const selectedFile = useSelectedFile();

    // selectedTaggedFileIndex should be defined at this point, but falling back to 0 just in case
    const mediaTaggedVariables = useMemo(
        () => getMediaTaggedVariables(taggedFiles[selectedTaggedFileIndex ?? 0]),
        [taggedFiles, selectedTaggedFileIndex],
    );

    const skipMediaTaggedQuery = useMemo(
        () =>
            !hasValue(selectedTaggedFileIndex) ||
            !taggedFiles[selectedTaggedFileIndex]?.isPersisted ||
            hasTaggedChildren(taggedFiles[selectedTaggedFileIndex].taggedChildren) ||
            // See https://famlyapp.atlassian.net/browse/FBS-6787 about the following check
            !isUUID(taggedFiles[selectedTaggedFileIndex].fileId),
        [selectedTaggedFileIndex, taggedFiles],
    );

    const {data: mediaTaggedData, loading: isFetchingTaggedChildren} = useResilientQuery<
        MediaTaggedQuery,
        MediaTaggedQueryVariables
    >(MediaTagged, {
        skip: skipMediaTaggedQuery,
        variables: mediaTaggedVariables,
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (!selectedFile || !data) {
                return;
            }

            const childrenMedia = data.mediaTagging.mediaTagged.list.childrenMedia.find(
                media => media.mediaId === selectedFile.id,
            );

            const taggedChildrenIds = childrenMedia?.children.map(child => child.id);

            /**
             * This query triggers again when there're no tags for the selectedMedia.
             * If it was already init, we don't want to override the tags in case they're being edited
             * (since tags are saved only at the end of the flow)
             */
            if (
                !childrenMedia ||
                !taggedChildrenIds ||
                !isNonEmpty(taggedChildrenIds) ||
                Boolean(didInitTaggedMedia?.includes(selectedFile.id))
            ) {
                return;
            }

            initTaggedChildren({fileId: childrenMedia.mediaId, taggedChildrenIds});
        },
    });

    return {mediaTagged: mediaTaggedData, isFetchingTaggedChildren};
};

export const isUUID = (uuid: string) => {
    const uuidPattern = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;
    const version1Pattern =
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const version3Pattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-3[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    const version5Pattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-5[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

    return (
        uuidPattern.test(uuid) || version1Pattern.test(uuid) || version3Pattern.test(uuid) || version5Pattern.test(uuid)
    );
};
